import { Injectable } from '@angular/core';
import { CheckRideOwnershipTypeAllItems, FlightSchoolTypeAllItems } from '../../../meta-data/app-meta-data.service';
import { AppPermissions } from '../../../meta-data/app-permissions.enum';
import { PilexosUserTypeIds } from '../../shared/models/pilexos-user-type.enum';
import { AdminManagerService } from '../../shared/services/admin-manager.service';
import { SchoolFeatures } from '../../schools/models/school-features.model';
import { SchoolRepositoryService } from '../../schools/services/school-repository.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ReportAccessConfig } from '../models/report-access-config.model';

@Injectable({
    providedIn: 'root'
})
export class ReportAccessService {
    constructor(
        private adminManagerService: AdminManagerService,
        private schoolRepositoryService: SchoolRepositoryService
    ) { }

    resolveAccessConfig(): Observable<ReportAccessConfig> {
        const pilexosUser = this.adminManagerService.getPilexosUser();
        const isSchoolUser = this.isSchoolUser(pilexosUser?.UserTypeId);

        if (!isSchoolUser) {
            return of(this.buildAccessConfig(pilexosUser, null));
        }

        return this.schoolRepositoryService.getSchoolFeatures()
            .pipe(
                map(schoolFeatures => this.buildAccessConfig(pilexosUser, schoolFeatures)),
                catchError(() => of(this.buildAccessConfig(pilexosUser, {
                    FlightSchoolTypeId: FlightSchoolTypeAllItems.Standard
                } as SchoolFeatures)))
            );
    }

    private buildAccessConfig(pilexosUser: any, schoolFeatures: SchoolFeatures | null): ReportAccessConfig {
        const isPilexosAdmin = pilexosUser?.hasPermission(AppPermissions.pilexosAdmin) || false;
        const isSchoolUser = this.isSchoolUser(pilexosUser?.UserTypeId);
        const isExaminer = pilexosUser?.UserTypeId === PilexosUserTypeIds.pilotExaminer;
        const isOdaSchool = isSchoolUser && schoolFeatures?.FlightSchoolTypeId === FlightSchoolTypeAllItems.ODA;

        if (isSchoolUser && isOdaSchool) {
            return {
                mode: 'odaSchool',
                pilexosUser: pilexosUser,
                schoolFeatures: schoolFeatures,
                isSchoolUser: true,
                isOdaSchool: true,
                isExaminer: false,
                isPilexosAdmin: isPilexosAdmin,
                hideFilterKeys: [],
                hidePivotDimensionKeys: [],
                labelOverrides: {
                    examiner: 'Unit Member'
                },
                autoFilterValues: {
                    checkRideOwnershipTypeId: CheckRideOwnershipTypeAllItems.FlightSchool,
                    flightSchoolOrganizationId: schoolFeatures?.FlightSchoolOrganizationId || pilexosUser?.flightSchoolOrganizationId
                },
                adminExperienceDeferred: isPilexosAdmin
            };
        }

        if (isSchoolUser) {
            return {
                mode: 'standardSchool',
                pilexosUser: pilexosUser,
                schoolFeatures: schoolFeatures,
                isSchoolUser: true,
                isOdaSchool: false,
                isExaminer: false,
                isPilexosAdmin: isPilexosAdmin,
                hideFilterKeys: ['flightSchool'],
                hidePivotDimensionKeys: ['flightSchool'],
                labelOverrides: {
                    examiner: 'Examiner / Check Instructor'
                },
                autoFilterValues: {
                    checkRideOwnershipTypeId: CheckRideOwnershipTypeAllItems.FlightSchool,
                    flightSchoolOrganizationId: schoolFeatures?.FlightSchoolOrganizationId || pilexosUser?.flightSchoolOrganizationId
                },
                adminExperienceDeferred: isPilexosAdmin
            };
        }

        if (isExaminer) {
            return {
                mode: 'examiner',
                pilexosUser: pilexosUser,
                schoolFeatures: null,
                isSchoolUser: false,
                isOdaSchool: false,
                isExaminer: true,
                isPilexosAdmin: isPilexosAdmin,
                hideFilterKeys: ['examiner', 'flightSchool', 'flightInstructor'],
                hidePivotDimensionKeys: ['examiner', 'flightSchool', 'flightInstructor'],
                labelOverrides: {},
                autoFilterValues: {
                    checkRideOwnershipTypeId: CheckRideOwnershipTypeAllItems.Examiner,
                    pilotExaminerId: pilexosUser?.pilotExaminerId
                },
                adminExperienceDeferred: isPilexosAdmin
            };
        }

        if (isPilexosAdmin) {
            return {
                mode: 'admin',
                pilexosUser: pilexosUser,
                schoolFeatures: schoolFeatures,
                isSchoolUser: false,
                isOdaSchool: false,
                isExaminer: false,
                isPilexosAdmin: true,
                hideFilterKeys: ['flightInstructor'],
                hidePivotDimensionKeys: ['flightInstructor'],
                labelOverrides: {},
                autoFilterValues: {},
                adminExperienceDeferred: true
            };
        }

        return {
            mode: 'default',
            pilexosUser: pilexosUser,
            schoolFeatures: schoolFeatures,
            isSchoolUser: false,
            isOdaSchool: false,
            isExaminer: false,
            isPilexosAdmin: false,
            hideFilterKeys: ['flightInstructor'],
            hidePivotDimensionKeys: ['flightInstructor'],
            labelOverrides: {},
            autoFilterValues: {}
        };
    }

    private isSchoolUser(userTypeId: PilexosUserTypeIds | null | undefined): boolean {
        const schoolUserTypes = [
            PilexosUserTypeIds.flightSchool,
            PilexosUserTypeIds.checkInstructor,
            PilexosUserTypeIds.flightInstructor
        ];

        return !!userTypeId && schoolUserTypes.includes(userTypeId);
    }
}
